import {
    appData,
    state,
    tempConfigData,
    tempUsersData,
    tempTeamsData,
    setTempUsersData,
    setTempTeamsData,
    clearTempConfigData,
    clearTempUsersData,
    clearTempTeamsData,
    clearTempUserDeactivationDraft,
    setTempUserDeactivationDraft,
} from "../../app/state/state.js";
import { captureUserSettingsBaseline, preflightUserSettingsChanges } from "./userAccessSettings.js";
import { saveSettingsChanges } from "./settingsApi.js";
import { navigate } from "../../app/routing/navigationController.js";
import {
    renderUserSettingsTab,
    handleUserSettingsChange,
    handleUserSettingsButtonClick,
    syncUserSettingsStateFromDom,
    handleUserSettingsSaveSuccess,
    resetUserSettingsState,
    syncUserSettingsFromLiveData,
} from "./userSettings.js";
import {
    renderPersonalNotificationSettingsPanel,
} from "./personalNotificationSettings.js";
import {
    renderAdminNotificationSettingsTab,
    setAdminNotificationSettingsUpdateSaveButton,
} from "./adminNotificationSettings.js";
import {
    handleCalendarSettingsButtonClick,
    handleCalendarSettingsChange,
    renderCalendarSettings,
    validateCalendarSettingsDraft,
} from "./calendarSettings.js";
import {
    handleLeaveSettingsButtonClick,
    handleLeaveSettingsChange,
    renderLeaveSettings,
    validateLeaveSettingsDraft,
} from "./leaveSettings.js";
import {
    handleBusinessCalendarSettingsButtonClick,
    handleBusinessCalendarSettingsChange,
    renderBusinessCalendarSettings,
    validateBusinessCalendarSettingsDraft,
} from "./businessCalendarSettings.js";
import {
    renderGeneralTab as renderGeneralSettingsTab,
    handleGeneralSettingsButtonClick,
    handleGeneralSettingsConfigChange,
    setGeneralSettingsUpdateSaveButton,
} from "./generalSettings.js";
import {
    renderTeamsTab as renderTeamsSettingsTab,
    syncTeamsSettingsFromLiveData as syncLiveTeamsSettings,
    setTeamSettingsUpdateSaveButton,
    handleTeamSettingsClick,
} from "./teamSettings.js";
import {
    renderChecklistsTab as renderChecklistSettingsTab,
    handleChecklistSettingsButtonClick,
    bindChecklistTemplateUpdates,
} from "./checklistSettings.js";
import {
    renderCategoryConfigTab,
    handleCategorySettingsChange,
    handleCategorySettingsButtonClick,
    resetCategorySettingsState,
} from "./categorySettings.js";
import {
    handleMaintenanceSettingsButtonClick,
    handleMaintenanceSettingsChange,
    renderMaintenanceSettingsTab,
} from "./maintenanceSettings.js";
import {
    handleLabsSettingsButtonClick,
    handleLabsSettingsChange,
    renderLabsSettingsTab,
} from "./labsSettings.js";
import {
    handleRecallsSettingsButtonClick,
    handleRecallsSettingsChange,
    renderRecallsSettingsTab,
    validateRecallsSettingsDraft,
} from "./recallsSettings.js";
import {
    renderFeatureNavSwitcher,
} from "../../shared/ui/featureNavSwitcher.js";
import {
    renderMobileRouteHeaderActions,
} from "../../shared/ui/mobileRouteHeaderActions.js";
import { applyBrandingVariables } from "../../shared/ui/brandingTokens.js";
import {
    discardOrderingSettingsDraft,
    invalidateOrderingSettingsRender,
    renderOrderingSettings,
    validateOrderingSettingsDraft,
} from "./orderingSettings.js";
import {
    discardComplianceSettingsDraft,
    invalidateComplianceSettingsRender,
    renderComplianceSettings,
    saveComplianceSettingsDraft,
    validateComplianceSettingsDraft,
} from "./complianceSettings.js";

let currentSettingsTab = "general";

function normalizeSettingsTabId(tabId) {
    if (tabId === "leave" || tabId === "businessCalendar") {
        return "calendar";
    }
    return tabId || "general";
}

function getSettingsTabItems(isAdmin) {
    return [
        { id: "general", label: "General" },
        ...(isAdmin ? [
            { id: "users", label: "Users" },
            { id: "teams", label: "Teams" },
            { id: "calendar", label: "Calendar" },
            { id: "notifications", label: "Notifications" },
            { id: "checklists", label: "Checklists" },
        ] : []),
        { id: "tasks", label: "Tasks" },
        { id: "patients", label: "Patients" },
        ...(isAdmin ? [
            { id: "labs", label: "Labs" },
            { id: "recalls", label: "Recalls" },
        ] : []),
        { id: "projects", label: "Projects" },
        { id: "assets", label: "Assets" },
        ...(isAdmin ? [
            { id: "maintenance", label: "Maintenance" },
            { id: "compliance", label: "Compliance" },
            { id: "ordering", label: "Ordering" },
        ] : []),
        ...(isAdmin ? [{ id: "drugs", label: "Drugs" }] : []),
    ];
}

function updateSaveButton(isDirty) {
    const buttons = document.querySelectorAll(".settings-save-btn");
    if (buttons.length === 0) {return;}

    if (isDirty) {
        state.hasUnsavedChanges = true;
        buttons.forEach((btn) => {
            btn.disabled = false;
            btn.classList.remove(
                "bg-gray-300",
                "text-gray-500",
                "cursor-not-allowed",
            );
            btn.classList.add(
                "shadow-md",
            );
        });
        return;
    }

    state.hasUnsavedChanges = false;
    buttons.forEach((btn) => {
        btn.disabled = true;
        btn.classList.remove(
            "shadow-md",
        );
        btn.classList.add("bg-gray-300", "text-gray-500", "cursor-not-allowed");
    });
}

setAdminNotificationSettingsUpdateSaveButton(updateSaveButton);
setGeneralSettingsUpdateSaveButton(updateSaveButton);
setTeamSettingsUpdateSaveButton(updateSaveButton);
bindChecklistTemplateUpdates(
    () => renderCurrentTabContent(),
    () => currentSettingsTab,
);

export { renderPersonalNotificationSettingsPanel as renderNotificationSettingsPanel };

/**
 * Renders the main Settings view shell and active tab.
 */
export function renderSettingsView() {
    const content = document.getElementById("settings-content");
    if (!content) {return;}
    applyBrandingVariables(content, appData);

    if (!appData.config) {
        content.innerHTML =
            '<p class="text-gray-500">Loading configuration...</p>';
        return;
    }

    if (!tempConfigData) {
        content.innerHTML = `
            <p class="text-red-500">
                Error: Settings data not initialized. Try navigating away and back.
            </p>
        `;
        return;
    }

    if ((!tempUsersData || tempUsersData.length === 0) && appData.users) {
        setTempUsersData(appData.users);
        captureUserSettingsBaseline(appData.users);
    }

    if (tempTeamsData === null && Array.isArray(appData.teams) &&
        appData.teams.length > 0) {
        setTempTeamsData(appData.teams);
    }

    if (state.settingsReturnContext?.tab) {
        currentSettingsTab = normalizeSettingsTabId(
            state.settingsReturnContext.tab,
        );
        state.settingsReturnContext = null;
    }

    const isAdmin = state.currentUserProfile?.role === "admin";
    currentSettingsTab = normalizeSettingsTabId(currentSettingsTab);
    const tabItems = getSettingsTabItems(isAdmin);
    if (!tabItems.some((item) => item.id === currentSettingsTab)) {
        currentSettingsTab = "general";
    }
    content.innerHTML = `
        <div class="flex flex-col h-full">
            <div class="settings-shell-top">
                <div class="settings-title-row flex items-center justify-between gap-3 md:block">
                    <h2 class="text-2xl font-bold text-gray-800">Settings</h2>
                    ${renderMobileRouteHeaderActions()}
                </div>

                ${renderFeatureNavSwitcher({
                    id: "settings-mobile-section-switcher",
                    label: "Settings section",
                    items: tabItems,
                    activeId: currentSettingsTab,
                })}

                <div class="settings-actions">
                    <button type="button" id="cancel-settings-btn" class="cancel-settings-btn settings-action-btn settings-cancel-btn bg-gray-200 hover:bg-gray-300 text-gray-800 font-bold rounded-lg">Cancel</button>
                    <button type="button" id="save-settings-btn" disabled class="settings-action-btn settings-save-btn bg-gray-300 text-gray-500 font-bold rounded-lg cursor-not-allowed transition-colors duration-200">Save Settings</button>
                </div>

                <div id="settings-tabs-container" class="hidden max-w-full gap-8 overflow-x-auto border-b border-gray-200 md:flex md:w-fit">
                    ${tabItems.map((item) => renderTabButton(item.id, item.label)).join("")}
                </div>
            </div>

            <div id="settings-tab-content" class="settings-control-surface flex-grow overflow-y-auto pb-10"></div>
            <div class="settings-mobile-action-spacer" aria-hidden="true"></div>
            <div class="settings-mobile-action-bar">
                <button type="button" class="cancel-settings-btn settings-action-btn settings-cancel-btn bg-gray-200 hover:bg-gray-300 text-gray-800 font-bold rounded-lg">Cancel</button>
                <button type="button" disabled class="settings-action-btn settings-save-btn bg-gray-300 text-gray-500 font-bold rounded-lg cursor-not-allowed transition-colors duration-200">Save Settings</button>
            </div>
        </div>
    `;

    renderCurrentTabContent();
    updateSaveButton(state.hasUnsavedChanges);

    document.querySelectorAll(".settings-save-btn").forEach((saveBtn) => {
        saveBtn.onclick = async () => {
            syncUserSettingsStateFromDom();
            if (!validateRecallsSettingsDraft()) {return;}
            if (!validateCalendarSettingsDraft()) {return;}
            if (!validateLeaveSettingsDraft()) {return;}
            if (!validateBusinessCalendarSettingsDraft()) {return;}
            if (!await validateOrderingSettingsDraft()) {return;}
            if (!validateComplianceSettingsDraft()) {return;}
            if (!await preflightUserSettingsChanges()) {return;}
            if (!await saveComplianceSettingsDraft()) {return;}
            const settingsSuccess = await saveSettingsChanges();

            if (settingsSuccess) {
                handleUserSettingsSaveSuccess();
                resetCategorySettingsState();
            }
            if (!settingsSuccess) {
                updateSaveButton(true);
                return;
            }
            updateSaveButton(false);
            renderSettingsView();
        };
    });

    document.querySelectorAll(".cancel-settings-btn").forEach((cancelBtn) => {
        cancelBtn.onclick = () => {
            clearTempConfigData();
            clearTempUsersData();
            clearTempTeamsData();
            clearTempUserDeactivationDraft();
            resetUserSettingsState();
            resetCategorySettingsState();
            discardOrderingSettingsDraft();
            discardComplianceSettingsDraft();
            updateSaveButton(false);
            navigate(state.originatingView || "tasks");
        };
    });

    const tabContainer = document.getElementById("settings-tabs-container");
    if (tabContainer) {
        tabContainer.addEventListener("click", (event) => {
            const btn = event.target.closest(".settings-tab-btn");
            if (!btn) {return;}

            currentSettingsTab = normalizeSettingsTabId(btn.dataset.tab);
            renderSettingsView();
        });
    }
    const mobileTabContainer = document.getElementById("settings-mobile-section-switcher");
    if (mobileTabContainer) {
        mobileTabContainer.addEventListener("click", (event) => {
            const btn = event.target.closest("[data-feature-nav-option]");
            if (!btn) {return;}

            currentSettingsTab = normalizeSettingsTabId(
                btn.dataset.featureNavOption,
            );
            renderSettingsView();
        });
    }

    const contentContainer = document.getElementById("settings-tab-content");
    if (!contentContainer) {return;}

    contentContainer.addEventListener("input", handleSettingsInputOrChange);
    contentContainer.addEventListener("change", handleSettingsInputOrChange);
    contentContainer.addEventListener("click", handleSettingsButtonClick);
}

function renderTabButton(id, label) {
    const isActive = currentSettingsTab === id;
    const activeClass = isActive
        ? "text-[var(--dfc-brand-accent)] font-semibold"
        : "text-gray-400 hover:text-[var(--dfc-brand-primary)]";

    return `
        <button class="settings-tab-btn py-2 transition-colors duration-200 focus:outline-none whitespace-nowrap ${activeClass}" data-tab="${id}">
            ${label}
        </button>
    `;
}

function renderCombinedCalendarSettings(container) {
    container.innerHTML = `
        <div class="max-w-5xl space-y-6">
            <div data-calendar-business-settings></div>
            <div data-calendar-visual-settings></div>
            <div data-calendar-leave-settings></div>
        </div>
    `;
    renderBusinessCalendarSettings(
        container.querySelector("[data-calendar-business-settings]"),
        { updateSaveButton },
    );
    renderCalendarSettings(
        container.querySelector("[data-calendar-visual-settings]"),
        { updateSaveButton },
    );
    renderLeaveSettings(
        container.querySelector("[data-calendar-leave-settings]"),
        { updateSaveButton },
    );
}

function renderCurrentTabContent() {
    const container = document.getElementById("settings-tab-content");
    if (!container) {return;}
    if (currentSettingsTab !== "ordering") {
        invalidateOrderingSettingsRender();
    }
    if (currentSettingsTab !== "compliance") {
        invalidateComplianceSettingsRender();
    }

    switch (currentSettingsTab) {
        case "general":
            renderGeneralSettingsTab(container);
            return;
        case "users":
            renderUserSettingsTab(container, { updateSaveButton });
            return;
        case "teams":
            renderTeamsSettingsTab(container);
            return;
        case "notifications":
            renderAdminNotificationSettingsTab(container);
            return;
        case "calendar":
            renderCombinedCalendarSettings(container);
            return;
        case "checklists":
            renderChecklistSettingsTab(container);
            return;
        case "patients":
            renderCategoryConfigTab(
                container,
                "Patient",
                "patientCategoryConfig",
                "patientStatus",
                "patientTypes",
                null,
                "paymentMethods",
            );
            return;
        case "projects":
            renderCategoryConfigTab(
                container,
                "Project",
                "projectCategoryConfig",
                "projectStatus",
                "projectTypes",
            );
            return;
        case "assets":
            renderCategoryConfigTab(
                container,
                "Asset",
                "assetCategoryConfig",
                "equipmentStatus",
                "assetTypes",
                null,
                null,
                "issueStatus",
            );
            return;
        case "tasks":
            renderCategoryConfigTab(
                container,
                "Task",
                "taskCategoryConfig",
                "taskStatus",
                "taskTypes",
            );
            return;
        case "labs":
            renderLabsSettingsTab(container, { updateSaveButton });
            return;
        case "maintenance":
            renderMaintenanceSettingsTab(container, { updateSaveButton });
            return;
        case "ordering":
            void renderOrderingSettings(container, {
                isCurrent: () => state.currentView === "settings" &&
                    currentSettingsTab === "ordering",
                updateSaveButton,
            });
            return;
        case "compliance":
            renderComplianceSettings(container, {
                isCurrent: () => state.currentView === "settings" &&
                    currentSettingsTab === "compliance",
                updateSaveButton,
            });
            return;
        case "recalls":
            renderRecallsSettingsTab(container, { updateSaveButton });
            return;
        case "drugs":
            import("../drugs/renderDrugsAdmin.js").then((module) => {
                module.renderDrugsSettings(container);
            });
            return;
        default:
            renderGeneralSettingsTab(container);
    }
}

function handleSettingsInputOrChange(event) {
    const target = event.target;

    if (target.id === "drug-settings-content" ||
        target.closest("#drug-settings-content")) {
        updateSaveButton(true);
        return;
    }

    if (handleUserSettingsChange(target)) {
        return;
    }

    if (
        currentSettingsTab === "general" &&
        handleGeneralSettingsConfigChange(
            target,
            () => renderGeneralSettingsTab(
                document.getElementById("settings-tab-content"),
            ),
            event.type,
        )
    ) {
        return;
    }

    if (
        currentSettingsTab === "labs" &&
        handleLabsSettingsChange(target, {
            updateSaveButton,
            rerenderCurrentTabContent: renderCurrentTabContent,
        })
    ) {
        return;
    }

    if (
        currentSettingsTab === "maintenance" &&
        handleMaintenanceSettingsChange(target, {
            updateSaveButton,
            rerenderCurrentTabContent: renderCurrentTabContent,
        })
    ) {
        return;
    }

    if (
        currentSettingsTab === "recalls" &&
        handleRecallsSettingsChange(target, {
            updateSaveButton,
            rerenderCurrentTabContent: renderCurrentTabContent,
        })
    ) {
        return;
    }

    if (
        currentSettingsTab === "calendar" &&
        (
            handleBusinessCalendarSettingsChange(target, {
                rerenderCurrentTabContent: renderCurrentTabContent,
            }) ||
            handleCalendarSettingsChange(target) ||
            handleLeaveSettingsChange(target, {
                rerenderCurrentTabContent: renderCurrentTabContent,
            })
        )
    ) {
        return;
    }

    handleCategorySettingsChange(target, {
        currentSettingsTab,
        updateSaveButton,
        rerenderCurrentTabContent: renderCurrentTabContent,
    });
}

async function handleSettingsButtonClick(event) {
    const target = event.target;

    if (await handleUserSettingsButtonClick(target)) {
        return;
    }

    if (
        currentSettingsTab === "general" &&
        await handleGeneralSettingsButtonClick(target)
    ) {
        return;
    }

    if (handleTeamSettingsClick(target)) {
        return;
    }

    if (handleChecklistSettingsButtonClick(target)) {
        return;
    }

    if (
        currentSettingsTab === "labs" &&
        handleLabsSettingsButtonClick(target, {
            updateSaveButton,
            rerenderCurrentTabContent: renderCurrentTabContent,
        })
    ) {
        return;
    }

    if (
        currentSettingsTab === "maintenance" &&
        handleMaintenanceSettingsButtonClick(target, {
            updateSaveButton,
            rerenderCurrentTabContent: renderCurrentTabContent,
        })
    ) {
        return;
    }

    if (
        currentSettingsTab === "recalls" &&
        await handleRecallsSettingsButtonClick(target, {
            updateSaveButton,
            rerenderCurrentTabContent: renderCurrentTabContent,
        })
    ) {
        return;
    }

    if (
        currentSettingsTab === "calendar" &&
        (
            handleBusinessCalendarSettingsButtonClick(target, {
                rerenderCurrentTabContent: renderCurrentTabContent,
            }) ||
            handleCalendarSettingsButtonClick(target) ||
            handleLeaveSettingsButtonClick(target, {
                rerenderCurrentTabContent: renderCurrentTabContent,
            })
        )
    ) {
        return;
    }

    await handleCategorySettingsButtonClick(target, {
        updateSaveButton,
        rerenderCurrentTabContent: renderCurrentTabContent,
    });
}

/**
 * Rerenders settings tabs when live team data changes.
 */
export function syncTeamsSettingsFromLiveData() {
    syncLiveTeamsSettings(renderCurrentTabContent);
}

/**
 * Rerenders Users settings when live invite data changes.
 */
export function syncUserInviteSettingsFromLiveData() {
    syncUserSettingsFromLiveData();
}
/**
 * @file Coordinates the Settings page shell, tabs, and delegated handlers.
 */

/**
 * Shows an explicitly opened access review in the existing Users workspace.
 * @param {object} payload Trusted review payload.
 * @returns {void}
 */
export function showUserAccessReviewWorkspace(payload) {
    setTempUserDeactivationDraft(payload);
    currentSettingsTab = "users";
    renderSettingsView();
    requestAnimationFrame(() => {
        const workspace = document.getElementById("user-deactivation-draft");
        workspace?.scrollIntoView({block: "start"});
        workspace?.querySelector("h3")?.focus();
    });
}
